
import { computed, defineComponent, onBeforeMount, PropType, reactive, toRaw } from 'vue'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'

// validation
import useVuelidate from '@vuelidate/core'

// types
import { SlaT } from '@/core/types/sla.types'
import { CityT } from '@/core/types/cities.types'

// constants
import { SlaTariffs, SLA_CARRIERS } from '@/core/constants/sla.constants'

// composable
import useHelpers from '@/common/composable/useHelpers'
import { DataState } from '@/core/classes/DataState.class'

// realisations
import { cities } from '@/core/realisations/cities.realisation'
import { CompanyT } from '@/core/types/companies.types'
import { companies } from '@/core/realisations/companies.realisation'
import { integer, required, requiredIf } from '@vuelidate/validators'

const PERIOD_OPTIONS = [
    { id: 1, name: 'Летний' },
    { id: 2, name: 'Зимний' },
]

export default defineComponent({
    components: {
        Modal,
        TheInput,
        TheSelect,
        Checkbox,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        isCreate: {
            type: Boolean,
            default: false,
        },
        isUpdate: {
            type: Boolean,
            default: false,
        },
        isDelete: {
            type: Boolean,
            default: false,
        },
        initialValue: {
            type: Object as PropType<SlaT>,
            default: null,
        },
    },
    emits: ['create', 'update', 'delete', 'cancel'],
    setup(props, { emit }) {
        const state = reactive({
            id: null as number | null,
            city_from: null as number | null,
            city_to: null as number | null,
            pickup: null as number | null,
            processing: null as number | null,
            transit: null as number | null,
            delivery: null as number | null,
            tariff: null as string | null,
            supplier_id: null as number | null,
            is_active: true,
            periodId: null as number | null,
            is_unique: false,
            companyId: null as number | null,
        })

        const data_state = reactive({
            from_cities: new DataState<CityT>({}),
            to_cities: new DataState<CityT>({}),
            companies: new DataState<CompanyT>({}),
        })

        const CreateSLAValidation = {
            city_from: { required },
            city_to: { required },
            pickup: { required, integer },
            processing: { required, integer },
            transit: { required, integer },
            delivery: { required, integer },
            tariff: { required },
            companyId: { required: requiredIf(() => state.is_unique) },
        }
        const state_form$ = useVuelidate(CreateSLAValidation, state)

        const loadFromCities = async (page: number, search?: string) => {
            if (props.isDelete) return

            data_state.from_cities.pagination.current_page = page

            try {
                data_state.from_cities.is_loading = true

                const data = await cities.getAllList(page, search)
                data_state.from_cities.list = data.data

                data_state.from_cities.pagination.total_pages = data.meta.last_page

                data_state.from_cities.is_failed = false
                data_state.from_cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.from_cities.failure.catchError(error)

                if (!data_state.from_cities.failure.error_state.is_cancel) {
                    data_state.from_cities.is_failed = true
                }
            } finally {
                if (!data_state.from_cities.failure.error_state.is_cancel) {
                    data_state.from_cities.is_loading = false
                }
            }
        }

        const loadToCities = async (page: number, search?: string) => {
            if (props.isDelete) return

            data_state.to_cities.pagination.current_page = page

            try {
                data_state.to_cities.is_loading = true

                const data = await cities.getAllList(page, search)
                data_state.to_cities.list = data.data

                data_state.to_cities.pagination.total_pages = data.meta.last_page

                data_state.to_cities.is_failed = false
                data_state.to_cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.to_cities.failure.catchError(error)

                if (!data_state.to_cities.failure.error_state.is_cancel) {
                    data_state.to_cities.is_failed = true
                }
            } finally {
                if (!data_state.to_cities.failure.error_state.is_cancel) {
                    data_state.to_cities.is_loading = false
                }
            }
        }

        const loadMoreFromCities = async (page: number) => {
            if (page > data_state.from_cities.pagination.total_pages) return
            data_state.from_cities.pagination.current_page = page

            try {
                data_state.from_cities.is_loading = true

                const response = await cities.getAllList(page)
                data_state.from_cities.list = data_state.from_cities.list.concat(response.data)

                data_state.from_cities.pagination.total_pages = response.meta.last_page

                data_state.from_cities.is_failed = false
                data_state.from_cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.from_cities.failure.catchError(error)

                if (!data_state.from_cities.failure.error_state.is_cancel) {
                    data_state.from_cities.is_failed = true
                }
            } finally {
                if (!data_state.from_cities.failure.error_state.is_cancel) {
                    data_state.from_cities.is_loading = false
                }
            }
        }

        const loadMoreToCities = async (page: number) => {
            if (page > data_state.to_cities.pagination.total_pages) return
            data_state.to_cities.pagination.current_page = page

            try {
                data_state.to_cities.is_loading = true

                const response = await cities.getAllList(page)
                data_state.to_cities.list = data_state.to_cities.list.concat(response.data)

                data_state.to_cities.pagination.total_pages = response.meta.last_page

                data_state.to_cities.is_failed = false
                data_state.to_cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.to_cities.failure.catchError(error)

                if (!data_state.to_cities.failure.error_state.is_cancel) {
                    data_state.to_cities.is_failed = true
                }
            } finally {
                if (!data_state.to_cities.failure.error_state.is_cancel) {
                    data_state.to_cities.is_loading = false
                }
            }
        }

        const searchFromCity = (search_query: string) => {
            // data_state.from_cities.pagination.current_page
            loadFromCities(1, search_query)
        }

        const searchToCity = (search_query: string) => {
            // data_state.to_cities.pagination.current_page
            loadToCities(1, search_query)
        }

        const loadCities = async () => {
            try {
                data_state.to_cities.is_loading = true
                data_state.from_cities.is_loading = true

                const data = await cities.getAllList(1)

                data_state.to_cities.list = data.data
                data_state.from_cities.list = data.data

                data_state.to_cities.pagination.total_pages = data.meta.last_page
                data_state.from_cities.pagination.total_pages = data.meta.last_page

                data_state.to_cities.is_failed = false
                data_state.from_cities.is_failed = false

                data_state.to_cities.failure.error_state.is_cancel = false
                data_state.from_cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.to_cities.failure.catchError(error)
                data_state.from_cities.failure.catchError(error)

                if (
                    !data_state.to_cities.failure.error_state.is_cancel &&
                    !data_state.from_cities.failure.error_state.is_cancel
                ) {
                    data_state.to_cities.is_failed = true
                    data_state.from_cities.is_failed = true
                }
            } finally {
                if (
                    !data_state.to_cities.failure.error_state.is_cancel &&
                    !data_state.from_cities.failure.error_state.is_cancel
                ) {
                    data_state.to_cities.is_loading = false
                    data_state.from_cities.is_loading = false
                }
            }
        }

        const unshiftFindedToCity = async (city: CityT) => {
            await loadToCities(data_state.to_cities.pagination.current_page)
            data_state.to_cities.list.unshift(city)
        }

        const unshiftFindedFromCity = async (city: CityT) => {
            await loadFromCities(data_state.from_cities.pagination.current_page)
            data_state.from_cities.list.unshift(city)
        }

        const modalTitle = computed(() => {
            if (props.isCreate) return 'Добавление SLA'
            if (props.isUpdate) return 'Редактирование SLA'
            if (props.isDelete) return 'Удаление SLA'
            return 'modal title'
        })

        const modalConfirmButtonText = computed(() => {
            if (props.isCreate) return 'Добавить'
            if (props.isUpdate) return 'Редактировать'
            if (props.isDelete) return 'Удалить'
            return 'Подтвердить'
        })

        /** create emit */
        const emitCreate = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            emit('create', toRaw(state))
        }

        /** update emit */
        const emitUpdate = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            emit('update', toRaw(state))
        }

        /** delete emit */
        const emitDelete = () => {
            emit('delete', state.id)
        }

        const emitManager = () => {
            if (props.isCreate) emitCreate()
            if (props.isUpdate) emitUpdate()
            if (props.isDelete) emitDelete()
        }

        const emitCancel = () => {
            emit('cancel')
        }

        const onTariffChange = () => {
            state.is_active = true
            state.supplier_id = null
        }

        const onUniqueChange = () => {
            state.companyId =
                props.isUpdate && props.initialValue.company ? props.initialValue.company.id : null
        }

        const loadCompanies = async (page: number, search?: string) => {
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true
                const response = await companies.getList({
                    page,
                    search,
                    statusId: 2,
                    isExtended: 0,
                })
                data_state.companies.list = response.data

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const loadMoreCompanies = async (page: number) => {
            if (page > data_state.companies.pagination.total_pages) return
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true

                const response = await companies.getList({ page, statusId: 2, isExtended: 0 })
                data_state.companies.list = data_state.companies.list.concat(response.data)

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        //

        onBeforeMount(async () => {
            loadCompanies(1)

            if (props.isCreate) {
                loadCities()
                return
            }

            if (!props.initialValue) return

            state.id = props.initialValue.id
            state.periodId = props.initialValue.periodId
            state.companyId = props.initialValue.company ? props.initialValue.company.id : null
            state.pickup = props.initialValue.pickup
            state.processing = props.initialValue.processing
            state.transit = props.initialValue.transit
            state.delivery = props.initialValue.delivery
            state.supplier_id = props.initialValue.supplierId
            state.is_active =
                props.initialValue.isActive === null ? true : props.initialValue.isActive

            const { findObjectByValue } = useHelpers()
            const tariff = findObjectByValue('name', props.initialValue.tariff, SlaTariffs)
            if (tariff) state.tariff = tariff.api_key

            if (props.isDelete) return

            await loadCities()

            const city_from = findObjectByValue<CityT>(
                'name',
                props.initialValue.fromCity,
                data_state.from_cities.list
            )

            const city_to = findObjectByValue<CityT>(
                'name',
                props.initialValue.toCity,
                data_state.to_cities.list
            )

            if (city_from) state.city_from = city_from.id
            if (city_to) state.city_to = city_to.id

            state_form$.value.$reset()
        })

        //

        const isExpress = computed(() => state.tariff === 'express')

        //

        return {
            state,
            data_state,
            state_form$,
            SlaTariffs,
            modalTitle,
            modalConfirmButtonText,
            emitManager,
            emitCancel,
            loadFromCities,
            loadToCities,
            loadMoreFromCities,
            loadMoreToCities,
            searchFromCity,
            searchToCity,
            unshiftFindedFromCity,
            unshiftFindedToCity,
            onTariffChange,
            onUniqueChange,
            loadCompanies,
            loadMoreCompanies,
            //
            isExpress,
            //
            SLA_CARRIERS,
            PERIOD_OPTIONS,
        }
    },
})
